import classNames from 'classnames';
import pluralize from 'pluralize';
import { ReactNode } from 'react';
import {
  ChevronDownWhiteIcon,
  GridDotsWhiteIcon,
  MenuWhiteIcon,
  SearchGrayIcon,
} from '../../icons';
import { DropdownMenu, DropdownMenuItem } from '../DropdownMenu';
import { PopoverMenu } from '../PopoverMenu';
import { SearchField } from '../SearchField';
import { Skeleton } from '../Skeleton';
import { AppSelectorInfo } from './types';

interface HeaderProps {
  isOpened: boolean;
  label?: ReactNode;
  appSelectorInfo?: AppSelectorInfo;
  minimizable: boolean;
  handleChangeOpen: () => void;
  headerMenuItems?: DropdownMenuItem[];
  search: string;
  headerMenuSelectedItem?: DropdownMenuItem & { labelCurrent?: ReactNode };
  isHeaderMenuItemsLoading?: boolean;
  disabledHeaderMenu?: boolean;
  resolveItemsCount: number;
  searchField?: boolean;
  setSearch: (value: string) => void;
}

const Header = ({
  label,
  search,
  isOpened,
  setSearch,
  minimizable,
  searchField,
  appSelectorInfo,
  headerMenuItems,
  handleChangeOpen,
  resolveItemsCount,
  headerMenuSelectedItem,
  isHeaderMenuItemsLoading,
  disabledHeaderMenu,
}: HeaderProps) => {
  return (
    <div
      className={classNames(
        'w-full flex flex-col items-center justify-center pt-4 border-white border-opacity-12',
        {
          'px-4 border-b space-y-6 pb-6': !searchField,
          'space-y-2': searchField,
        },
      )}
    >
      <div
        className={classNames('w-full flex items-center justify-between', {
          'border-b border-[rgba(255,_255,_255,_0.12)] pb-3 px-4': searchField,
        })}
      >
        {isOpened && <div className="text-[24px] font-serif leading-6 font-semibold">{label}</div>}
        <div className="h-6 flex flex-row gap-4 items-center">
          {isOpened && appSelectorInfo && (
            <PopoverMenu
              classes={{ container: '!w-auto z-10', panel: 'w-[230px] !left-[-45px]' }}
              button={<img src={GridDotsWhiteIcon} alt="app selector" />}
              items={appSelectorInfo.items}
            />
          )}
          {minimizable && (
            <button onClick={handleChangeOpen}>
              <img src={MenuWhiteIcon} alt="Menu" className="select-none" />
            </button>
          )}
        </div>
      </div>
      {headerMenuItems && (
        <DropdownMenu
          items={headerMenuItems || []}
          placement="bottom-start"
          className="w-[280px] shadow-lg border"
          selectedValue={headerMenuSelectedItem?.value}
          loading={isHeaderMenuItemsLoading}
          disabled={disabledHeaderMenu}
          button={
            <div
              className={classNames(`flex items-center`, {
                'justify-between': isOpened,
                'justify-center': !isOpened,
                'w-full': !searchField,
                'w-[calc(100%-32px)] pb-2': searchField,
              })}
            >
              <div className="flex items-center">
                {isOpened && (
                  <div className="text-[14px] leading-[22px]">
                    {isHeaderMenuItemsLoading ? (
                      <Skeleton width={140} className="mt-1 !bg-neutral-600" />
                    ) : (
                      headerMenuSelectedItem?.labelCurrent || headerMenuSelectedItem?.label
                    )}
                  </div>
                )}
              </div>
              {isOpened && !disabledHeaderMenu && !isHeaderMenuItemsLoading && (
                <button>
                  <img
                    className="select-none w-4 h-4 min-w-4 min-h-4"
                    src={ChevronDownWhiteIcon}
                    alt="Loading..."
                  />
                </button>
              )}
            </div>
          }
        />
      )}
      {searchField && (
        <SearchField
          allowClear
          searchTimeout={10}
          icon={SearchGrayIcon}
          placeholder="Search page"
          onChange={setSearch}
          className="py-0 !bg-gray-900 text-white !rounded [&_input]:!min-h-8 [&_input]:!h-8 w-[calc(100%-16px)] hover:border-gray-500 border-[rgba(255,_255,_255,_0.12)]"
        />
      )}
      {search && (
        <div className="text-xs self-start px-4 text-gray-500">
          {resolveItemsCount
            ? `${resolveItemsCount} ${pluralize('result', resolveItemsCount)}`
            : 'No results'}
        </div>
      )}
    </div>
  );
};

export default Header;
